import {
  HERO_CATALOG,
  allPartIds,
  customHeroIds,
  heroBaseKey,
  heroLookKey,
  heroPartKey,
  OVERLAY_NONE,
  REWARD_ONLY_ACC,
  overlayAllowed,
  type OverlayCategory,
} from "@greg-chick/shared";

/** 커스터마이저 단계에서 다루는 카테고리. 몸·옷은 통짜 룩, 모자·소품은 그 위 오버레이. */
export type PickCategory = "body" | "outfit" | OverlayCategory;

/** 매니페스트에 그 키의 그림이 게시돼 있는가. */
type HasArt = (key: string) => boolean;

/**
 * 이 단계에서 **고를 수 있는** 옵션들.
 *
 * 규칙이 카테고리마다 다르고 아이 안전과도 얽혀 있어 컴포넌트 밖으로 뺐다(테스트 가능하게):
 * - 몸·옷: 그 조합의 통짜 룩(hero.look.<몸>_<옷>)이 게시된 것만. 아이 판정은 기본 옷(o0) 기준 —
 *   옷을 바꿔도 "아이 고르기" 칸의 모습이 안 변해야 헷갈리지 않는다.
 * - 모자·소품: 그림이 하나라도 있으면 맨 앞에 "안 씀"을 붙인다. 벗을 수 없으면 되돌릴 방법이 없다.
 *   단, 지금 입은 옷에 이미 모자·가방이 그려져 있으면(겨울·탐험가·우비) 그 단계는 아예 없앤다.
 * - 완주 메달(a4)은 제외 — 랠리를 끝내면 hero.badge.medal 이 자동으로 걸린다.
 *   고를 수 있게 두면 안 끝내고도 달 수 있고, 끝냈다면 메달이 둘이 된다.
 */
/**
 * 아이에게 보여줄 **완성 캐릭터** 목록 — 그림이 있는 것만.
 *
 * 🔴 예전엔 카탈로그를 그대로 보여줬다. 운영자용 빈 자리(explorer_f~i)를 열면서
 * **그림도 없는 빈 칸 넷이 아이 화면에 나오게** 됐다 — 모자(c5~c8)에서 이미 겪은 문제라
 * 같은 방식으로 막는다. 정지 그림이 있는 것만 고를 수 있다.
 */
export function pickableBases(hasArt: HasArt, assetKeys: Iterable<string> = []): string[] {
  return [...(HERO_CATALOG.base as readonly string[]), ...customHeroIds(assetKeys)]
    .filter((b) => b !== "custom")
    .filter((b) => hasArt(heroBaseKey(b)));
}

export function pickableOptions(
  cat: PickCategory,
  body: string,
  outfit: string,
  hasArt: HasArt,
  assetKeys: Iterable<string> = [],
): string[] {
  // 모자·소품은 **운영자가 만든 것까지** 센다(매니페스트가 목록이다).
  // 몸·옷은 통짜 룩 그림이 고정 조합이라 카탈로그 그대로.
  const all =
    cat === "hair" || cat === "acc"
      ? allPartIds(cat, assetKeys)
      : (HERO_CATALOG[cat] as readonly string[]);
  if (cat === "body") return all.filter((o) => hasArt(heroLookKey(o, "o0")));
  if (cat === "outfit") return all.filter((o) => hasArt(heroLookKey(body, o)));
  // 지금 입은 옷에 이미 모자(가방)가 그려져 있으면 그 단계는 통째로 없앤다 — 얹어봐야 두 개가 된다.
  if (!overlayAllowed(cat, outfit)) return [];
  const none = OVERLAY_NONE[cat];
  const picked = all.filter((o) => o !== REWARD_ONLY_ACC && o !== none && hasArt(heroPartKey(cat, o)));
  return picked.length ? [none, ...picked] : [];
}

/**
 * 🧒 **아이가 빈 화면에 갇히지 않게** — 고르기 화면이 무엇을 보여줄지.
 *
 * 세 가지가 다 「운영자가 아직 안 올렸을 때」를 다룬다. 안 다루면 아이는
 * **아무것도 없는 단계**나 **아무것도 못 고르는 탭**에 서게 되고, 오류는 안 난다.
 */

/**
 * 그림이 하나도 안 올라온 단계는 **아예 숨긴다.**
 *
 * 🔴 남겨 두면 아이가 「모자」를 눌렀는데 빈 칸만 나온다 — 뒤로 갈 줄 모르는 나이다.
 * 운영자에게도 표시가 안 나므로(관리자에서는 슬롯이 비어 있을 뿐) 아무도 모른 채 남는다.
 */
export function visibleSteps<T extends { cat: PickCategory }>(
  all: readonly T[],
  count: (cat: PickCategory) => number,
): T[] {
  return all.filter((s) => count(s.cat) > 0);
}

/**
 * 「직접 꾸미기」로 갈지. 아이가 골랐거나, **완성 캐릭터가 하나도 없으면** 그쪽으로 간다.
 *
 * 🔴 폴백이 없으면: 운영자가 완성 캐릭터를 아직 안 올린 새 환경에서 고를 것이 하나도 없는
 * 탭이 열린다 — 아이는 시작조차 못 한다(M1 은 서버 없이도 돌아야 한다는 불변식 3과 같은 결).
 */
export function isCustomMode(base: string, presetCount: number): boolean {
  return base === "custom" || presetCount === 0;
}

/**
 * 캐릭터에 보여줄 이름 — **운영자가 붙인 이름이 먼저**다.
 *
 * 운영자가 만든 캐릭터는 코드에 이름표가 있을 수 없다(우리가 모르는 캐릭터다).
 * 그림을 올릴 때 적은 이름이 남으므로 그걸 쓴다. 없으면 기본 이름, 그것도 없으면 id —
 * **빈 이름은 안 낸다**(이름 없는 칸을 아이가 고를 수는 없다).
 */
export function baseLabelOf(base: string, alt?: string, builtIn?: string): string {
  return alt?.trim() || builtIn || base;
}
